<script lang="ts">
  import { apiClient } from '@/utils/api';
  import VerifiedBadge from '@/components/ui/VerifiedBadge.svelte';
  import SuggestedPeople from '@/components/ui/SuggestedPeople.svelte';
  import AdSlot from '@/components/ads/AdSlot.svelte';
  import { shouldShowAdAfter, IN_FEED_SLOT, IN_FEED_LAYOUT_KEY } from '@/lib/ads';

  /**
   * 소셜 피드 — 2026-08-17 시안 A(단일 컬럼) 확정.
   *
   * 상시 사이드바가 없다. 그래서 추천 인물이 보이는 곳은
   *   ① 팔로잉 탭이 비었을 때  ② 인물 디렉터리(P5)  ③ 온보딩(P5)
   * 뿐이고, ①이 콜드 스타트의 사실상 유일한 유입 경로다.
   *
   * 데이터는 전부 /api/v1/feed 에서 받는다 — Inertia props 로 초기 데이터를
   * 따로 내려주면 가시성 규칙이 두 곳에 생겨 한쪽이 조용히 낡는다(FeedController 주석).
   */
  type Tab = 'following' | 'recommended' | 'latest';

  type Post = {
    id: string;
    title: string;
    excerpt?: string | null;
    createdAt: string;
    likesCount?: number;
    commentsCount?: number;
    board?: { name?: string; slug?: string } | null;
    categories?: Array<{ name: string }>;
    user?: {
      id?: string;
      username?: string | null;
      name?: string;
      nickname?: string | null;
      avatarUrl?: string | null;
      // 🔴 서버 `author_json` 이 주는 이름 그대로다(snake→camel 변환만 거친다).
      //    `position`·`organizationName` 은 서버에 없는 필드였고, 그래서 소속이
      //    한 번도 표시되지 않았다(codex P2, 2026-08-18).
      organization?: string | null;
      department?: string | null;
      team?: string | null;
      platformVerified?: boolean;
      companyVerified?: boolean;
    } | null;
  };

  let {
    initial_tab = 'recommended',
    can_view_following = false
  }: { initial_tab?: Tab; can_view_following?: boolean } = $props();

  const ALL_TABS: Array<{ id: Tab; label: string }> = [
    { id: 'following', label: '팔로잉' },
    { id: 'recommended', label: '추천' },
    { id: 'latest', label: '최신' }
  ];
  // 눌러야 401 을 만나는 탭은 UI 가 아니라 함정이다 — 비로그인에게는 아예 안 보인다.
  const tabs = $derived(can_view_following ? ALL_TABS : ALL_TABS.filter((t) => t.id !== 'following'));

  // 초기값만 쓰는 것이 의도다 — 이후 탭은 사용자 조작으로만 바뀐다.
  // svelte-ignore state_referenced_locally
  let tab = $state<Tab>(initial_tab);
  let posts = $state<Post[]>([]);
  let cursor = $state<string | null>(null);
  let hasMore = $state(false);
  let loading = $state(true);
  let loadingMore = $state(false);
  let error = $state<string | null>(null);
  /** 서버가 팔로잉→추천으로 대체했을 때 그 사실. 조용히 바꾸지 않는다. */
  let fellBackTo = $state<Tab | null>(null);

  /** /api/v1/feed 의 meta — 커서·대체 사실이 여기로 온다. */
  type FeedMeta = {
    nextCursor?: string | null;
    hasMore?: boolean;
    tab?: Tab;
    fellBackTo?: Tab | null;
  };

  // apiClient 의 반환 타입에는 meta 가 없다(utils/api.ts). 공용 유틸을 바꾸면
  // 기존 호출부 전체에 파급되므로, 여기서 기대하는 형태를 명시해 좁힌다.
  type FeedResponse = {
    success: boolean;
    data?: { posts: Post[] };
    meta?: FeedMeta;
    message?: string;
  };

  async function fetchPage(nextCursor: string | null): Promise<FeedResponse | null> {
    const res = (await apiClient.get<{ posts: Post[] }>('/api/v1/feed', {
      params: nextCursor ? { tab, cursor: nextCursor } : { tab }
    })) as FeedResponse;
    if (!res.success) {
      error = res.message ?? '피드를 불러오지 못했습니다.';
      return null;
    }
    error = null;
    return res;
  }

  async function reload(next: Tab) {
    tab = next;
    loading = true;
    posts = [];
    cursor = null;
    fellBackTo = null;

    const res = await fetchPage(null);
    if (res) {
      posts = res.data?.posts ?? [];
      const meta: FeedMeta = res.meta ?? {};
      cursor = meta.nextCursor ?? null;
      hasMore = Boolean(meta.hasMore);
      // 대체 페이지에는 커서가 없다(서버가 주지 않는다) — 더 보기는 추천 탭으로 명시 전환한다.
      fellBackTo = meta.fellBackTo ?? null;
    }
    loading = false;
  }

  async function loadMore() {
    if (!cursor || loadingMore) return;
    loadingMore = true;
    const res = await fetchPage(cursor);
    if (res) {
      posts = [...posts, ...(res.data?.posts ?? [])];
      const meta: FeedMeta = res.meta ?? {};
      cursor = meta.nextCursor ?? null;
      hasMore = Boolean(meta.hasMore);
    }
    loadingMore = false;
  }

  // 최초 1회 로드. 이후 재조회는 탭 클릭·더 보기에서만 일어난다.
  // svelte-ignore state_referenced_locally
  reload(initial_tab);

  function authorName(p: Post) {
    return p.user?.nickname || p.user?.name || '알 수 없음';
  }
  function authorMeta(p: Post) {
    return [p.user?.organization, p.user?.department, p.user?.team].filter(Boolean).join(' · ');
  }
</script>

<svelte:head><title>피드 · AX Hub</title></svelte:head>

<!-- 단일 컬럼 — 본문 폭을 읽기 좋은 범위로 묶는다 -->
<div class="mx-auto w-full max-w-2xl px-4 py-4">
  <!-- 탭 -->
  <nav class="mb-4 flex border-b border-gray-200 dark:border-dark-border" aria-label="피드 종류">
    {#each tabs as t (t.id)}
      <button
        type="button"
        onclick={() => reload(t.id)}
        aria-current={tab === t.id ? 'page' : undefined}
        class="flex-1 border-b-2 py-2.5 text-[13px] font-semibold transition
               {tab === t.id
                 ? 'border-gray-900 text-gray-900 dark:border-dark-text-high dark:text-dark-text-high'
                 : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-dark-text-low'}"
      >
        {t.label}
      </button>
    {/each}
  </nav>

  {#if error}
    <p class="rounded-xl border border-error/30 bg-error-light px-4 py-3 text-sm text-error dark:bg-error/10">
      {error}
    </p>
  {/if}

  {#if loading}
    <div class="space-y-3">
      {#each [1, 2, 3] as i (i)}
        <div class="rounded-xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-secondary">
          <div class="mb-3 h-4 w-32 animate-pulse rounded bg-gray-100 dark:bg-dark-tertiary"></div>
          <div class="h-3 w-full animate-pulse rounded bg-gray-100 dark:bg-dark-tertiary"></div>
        </div>
      {/each}
    </div>
  {:else}
    <!-- 팔로잉이 비어 추천으로 대체된 경우 — 그 사실을 알린다 -->
    {#if fellBackTo}
      <div class="mb-3 rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-dark-border dark:bg-dark-secondary">
        <p class="text-[13px] leading-relaxed text-gray-600 dark:text-dark-text-medium">
          팔로우한 분의 글이 아직 없어 추천 글을 보여드립니다. 먼저 겪은 실무자를 찾아보세요.
        </p>
      </div>
      <div class="mb-3"><SuggestedPeople /></div>
    {/if}

    {#if posts.length === 0 && !fellBackTo}
      <div class="rounded-xl border border-gray-200 bg-white px-4 py-8 text-center dark:border-dark-border dark:bg-dark-secondary">
        <p class="text-[13px] leading-relaxed text-gray-600 dark:text-dark-text-medium">
          아직 표시할 글이 없습니다.
        </p>
      </div>
    {/if}

    <div class="space-y-3">
      {#each posts as p, i (p.id)}
        <article class="rounded-xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-secondary">
          <div class="mb-2 flex items-center gap-2">
            <div
              class="grid h-8 w-8 flex-none place-items-center rounded-full bg-gray-200 text-[11px]
                     font-bold text-gray-600 dark:bg-dark-tertiary dark:text-dark-text-medium"
            >
              {#if p.user?.avatarUrl}
                <img src={p.user.avatarUrl} alt="" class="h-8 w-8 rounded-full object-cover" />
              {:else}
                {authorName(p)[0]}
              {/if}
            </div>
            <div class="min-w-0">
              <!-- 목록에서는 아이콘 배지 (시안 C 확정) -->
              <div class="flex items-center gap-1 text-[13px] font-bold text-gray-900 dark:text-dark-text-high">
                {#if p.user?.username}
                  <a href={`/@${p.user.username}`} class="truncate hover:underline">{authorName(p)}</a>
                {:else}
                  <span class="truncate">{authorName(p)}</span>
                {/if}
                {#if p.user?.platformVerified}<VerifiedBadge kind="platform" />{/if}
                {#if p.user?.companyVerified}<VerifiedBadge kind="company" />{/if}
              </div>
              {#if authorMeta(p)}
                <p class="truncate text-[11px] text-gray-500 dark:text-dark-text-low">{authorMeta(p)}</p>
              {/if}
            </div>
          </div>

          <a href={`/posts/${p.id}`} class="block">
            <h2 class="mb-1 text-sm font-bold leading-snug text-gray-900 dark:text-dark-text-high">
              {p.title}
            </h2>
            {#if p.excerpt}
              <p class="text-[13px] leading-relaxed text-gray-600 dark:text-dark-text-medium">{p.excerpt}</p>
            {/if}
          </a>

          <div class="mt-2.5 flex items-center gap-3 text-[11px] text-gray-500 dark:text-dark-text-low">
            {#if p.board?.name}
              <span class="rounded-md bg-gray-100 px-1.5 py-0.5 dark:bg-dark-tertiary">{p.board.name}</span>
            {/if}
            <span>좋아요 {p.likesCount ?? 0}</span>
            <span>댓글 {p.commentsCount ?? 0}</span>
          </div>
        </article>
        {#if shouldShowAdAfter(i, posts.length)}
          <AdSlot variant="infeed" slot={IN_FEED_SLOT} format="fluid" layoutKey={IN_FEED_LAYOUT_KEY} />
        {/if}
      {/each}
    </div>

    {#if hasMore && cursor}
      <button
        type="button"
        onclick={loadMore}
        disabled={loadingMore}
        class="mt-4 w-full rounded-xl border border-gray-200 bg-white py-3 text-[13px] font-semibold
               text-gray-700 transition hover:bg-gray-50 disabled:opacity-50
               dark:border-dark-border dark:bg-dark-secondary dark:text-dark-text-medium"
      >
        {loadingMore ? '불러오는 중…' : '더 보기'}
      </button>
    {:else if fellBackTo}
      <!-- 대체 페이지는 커서가 없다. 계속 보려면 탭을 명시 전환한다. -->
      <button
        type="button"
        onclick={() => reload('recommended')}
        class="mt-4 w-full rounded-xl border border-primary bg-white py-3 text-[13px] font-semibold
               text-primary transition hover:bg-primary-50 dark:bg-dark-secondary"
      >
        추천 탭에서 더 보기
      </button>
    {/if}
  {/if}
</div>
